import type { ActionLog } from '@easyhelp/shared';
import CloseIcon from '@mui/icons-material/Close';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useId } from 'react';
import { formatDateTime } from '../../components/ticket';

/** Che cosa il log è, e che cosa non è (PRD-005 US-1, easycare DEC-101, DEC-042). */
export const ACTION_LOG_DISCLAIMER =
  "Solo gli eventi dei dieci minuti precedenti l'invio, raccolti sul dispositivo di chi ha segnalato. Non è l'audit trail della piattaforma: è parziale e lato client, non va usato come prova di accesso o di modifica.";

const clock = new Intl.DateTimeFormat('it-IT', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  timeZone: 'Europe/Rome',
});

export interface ActionLogViewerProps {
  log: ActionLog;
  code: string;
  onClose: () => void;
}

/**
 * Log delle ultime azioni (story #8): gli eventi in ordine di orario, ora italiana con i secondi;
 * la descrizione è testo semplice (React non interpreta HTML). Esc chiude e il focus torna al
 * comando che l'ha aperto.
 */
export function ActionLogViewer({ log, code, onClose }: ActionLogViewerProps) {
  const titleId = useId();
  const events = log.eventi
    .map((event, index) => ({ ...event, index, time: new Date(event.orario).getTime() }))
    .sort((a, b) => a.time - b.time || a.index - b.index);
  const first = events[0];
  const last = events.at(-1);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md" aria-labelledby={titleId}>
      <DialogTitle id={titleId} sx={{ pr: 8 }}>
        Log delle ultime azioni
        <Typography variant="body2" color="text.secondary" component="span" sx={{ display: 'block' }}>
          {code} · {events.length === 1 ? '1 evento' : `${events.length} eventi`} sul dispositivo di chi ha segnalato
          {first && last
            ? ` · dal ${formatDateTime(first.orario)} al ${formatDateTime(last.orario)} (ora italiana)`
            : ''}
        </Typography>
      </DialogTitle>
      <IconButton aria-label="Chiudi il log" onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12 }}>
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        <Alert severity="info" role="note" sx={{ mb: 3 }}>
          {ACTION_LOG_DISCLAIMER}
        </Alert>
        {events.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Il log è vuoto: nessun evento registrato sul dispositivo prima dell'invio.
          </Typography>
        ) : (
          <Box component="ol" aria-label="Eventi del log" sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {events.map((event) => (
              <Box
                component="li"
                key={event.index}
                sx={{ display: 'flex', gap: 4, py: 2, borderBottom: 1, borderColor: 'divider', alignItems: 'baseline' }}
              >
                <Typography
                  component="time"
                  dateTime={event.orario}
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}
                >
                  {clock.format(new Date(event.orario))}
                </Typography>
                <Typography variant="body2" sx={{ minWidth: 0, overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>
                  {event.descrizione}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
